import type { CmSeverity, CmSignalLink } from '@bloo-team/api-client'
import { caseSignalsGraph, type CaseGraphSignal } from './case-signal-graph'
import type { HuntGraph } from './hunt-graph'
import type { Signal, SignalSeverity } from './signal-model'
import { signalNodeId } from './signal-subgraphs'

/**
 * Several cases' signals as one graph, each case a box (docs/cases.md §Case
 * page · Relations, Clusters). The nodes are signals and their entities only
 * — linked signal → entity as on the Details graph (caseSignalsGraph) — and
 * every node knows the cases it belongs to: a signal the cases it is attached
 * to, an entity the cases of the signals it appears in. A node in several
 * cases is ONE node, drawn where those cases' boxes overlap. Pure.
 */

/** How a case sits in the graph: this case, a related one (or dismissed), a candidate, or a cluster member. */
export type NetworkKind = 'this' | 'related' | 'dismissed' | 'candidate' | 'cluster'

export interface NetworkCase {
  id: string
  caseNumber: string | null
  title: string
  status: string
  severity: CmSeverity
  kind: NetworkKind
  /** Clusters: hops from this case; null elsewhere. */
  hop: number | null
  /** Relations: how it was linked; null elsewhere. */
  linkedBy: 'auto' | 'manual' | null
}

/** At most this many cases' signal lists are read for one graph (each is one workspace read). */
export const NETWORK_CASES_CAP = 50

/** Candidates and dismissed relations are drawn fainter. */
export const fadedKind = (k: NetworkKind) => k === 'candidate' || k === 'dismissed'

/** The cases the graph reads: this case first, then the rest in the order given (nearest first), at most `cap`. */
export function pickCases(cases: readonly NetworkCase[], cap = NETWORK_CASES_CAP): NetworkCase[] {
  const seen = new Set<string>()
  const self = cases.filter((c) => c.kind === 'this')
  return [...self, ...cases.filter((c) => c.kind !== 'this')].filter((c) => !seen.has(c.id) && seen.add(c.id)).slice(0, cap)
}

const at = (l: CmSignalLink) => l.lastSeen ?? l.attachedAt ?? 0

/** The signals to read across the picked cases: each once, newest first (last seen, else attached). */
export function pickSignalIds(cases: readonly NetworkCase[], links: ReadonlyMap<string, readonly CmSignalLink[]>): string[] {
  const best = new Map<string, number>()
  for (const c of cases) for (const l of links.get(c.id) ?? []) best.set(l.signalId, Math.max(best.get(l.signalId) ?? -Infinity, at(l)))
  return [...best.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([id]) => id)
}

const SEVERITIES: readonly SignalSeverity[] = ['critical', 'high', 'medium', 'low', 'info']
const severityOf = (s: string | null): SignalSeverity => {
  const k = (s ?? '').toLowerCase() as SignalSeverity
  return SEVERITIES.includes(k) ? k : 'info'
}

export interface CaseNetwork {
  graph: HuntGraph
  /** Node id → the ids of the cases it belongs to (graph cases only). */
  members: Map<string, string[]>
  /** Case id → its signals in the graph. */
  signalCount: Map<string, number>
}

/**
 * The graph over `cases` from their signal lists (`links`, case id → attached
 * signals), drawing only `readIds` (the signals that were read, in order) with
 * their rows (`rows`: the signal, or null when it could not be read — a bare
 * node). A case with none of its signals drawn has no members.
 */
export function caseNetworkGraph(
  cases: readonly NetworkCase[],
  links: ReadonlyMap<string, readonly CmSignalLink[]>,
  readIds: readonly string[],
  rows: ReadonlyMap<string, Signal | null>,
): CaseNetwork {
  const drawn = new Set(readIds)
  const casesOf = new Map<string, string[]>()
  const linkOf = new Map<string, CmSignalLink>()
  for (const c of cases) {
    for (const l of links.get(c.id) ?? []) {
      if (!drawn.has(l.signalId)) continue
      const list = casesOf.get(l.signalId) ?? []
      if (!list.includes(c.id)) list.push(c.id)
      casesOf.set(l.signalId, list)
      if (!linkOf.has(l.signalId)) linkOf.set(l.signalId, l)
    }
  }
  const signals: CaseGraphSignal[] = readIds
    .filter((id) => casesOf.has(id))
    .map((uuid) => {
      const l = linkOf.get(uuid)
      return { uuid, title: l?.title ?? uuid, severity: severityOf(l?.severity ?? null), signal: rows.get(uuid) ?? null }
    })
  const graph = caseSignalsGraph(signals)

  const members = new Map<string, string[]>()
  const signalCount = new Map<string, number>(cases.map((c) => [c.id, 0]))
  for (const s of signals) {
    const ids = casesOf.get(s.uuid) ?? []
    members.set(signalNodeId(s.uuid), ids)
    for (const id of ids) signalCount.set(id, (signalCount.get(id) ?? 0) + 1)
  }
  // An entity belongs to every case of the signals it appears in, in the cases' order.
  const order = new Map(cases.map((c, i) => [c.id, i]))
  for (const e of graph.edges) {
    const [sig, ent] = e.from.startsWith('sig:') ? [e.from, e.to] : [e.to, e.from]
    const set = new Set([...(members.get(ent) ?? []), ...(members.get(sig) ?? [])])
    members.set(ent, [...set].sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0)))
  }
  return { graph, members, signalCount }
}
